import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Map, Navigation2, Play, Square, X, HandCoins, Truck, PackageX } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { RouteMap } from "../../components/routes/RouteMap";
import { getLocalOverride, getCashOverride, subscribe, cacheRoute, getCachedRoute } from "../../lib/driverSync";
import { SyncIndicator } from "../../components/driver/SyncIndicator";

function stopMeta(status) {
  if (status === 2) return { label: "Delivered", color: "bg-success-bg text-success" };
  if (status === 3) return { label: "Not Delivered", color: "bg-danger-bg text-danger" };
  return { label: "Pending", color: "bg-warning-bg text-warning" };
}

function packingSkippedMeta() {
  return { label: "Skipped by Packing", color: "bg-danger-bg text-danger" };
}

function mapsLink(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

export default function DriverRouteDetail() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, route: null });
  const [busy, setBusy] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [roadLegs, setRoadLegs] = useState(null);
  const watchIdRef = useRef(null);
  const [, forceRender] = useState(0);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get(`/driver/routes/${routeId}`)
      .then(({ data }) => {
        cacheRoute(routeId, data);
        setState({ loading: false, error: null, route: data });
      })
      .catch(async (error) => {
        const cached = await getCachedRoute(routeId);
        if (cached) {
          setState({ loading: false, error: null, route: cached, offline: true });
          return;
        }
        setState({ loading: false, error: getApiErrorMessage(error, "Could not load this route."), route: null });
      });
  }, [routeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe(() => forceRender((n) => n + 1)), []);

  // Lazily fetch road-following geometry the first time the driver opens the
  // map. Falls back to straight lines on any failure (e.g. offline / OSRM down).
  useEffect(() => {
    if (!mapOpen || roadLegs !== null) return;
    let cancelled = false;
    apiClient.get(`/driver/routes/${routeId}/geometry`)
      .then(({ data }) => { if (!cancelled) setRoadLegs(data?.legs || null); })
      .catch(() => { /* keep straight-line fallback */ });
    return () => { cancelled = true; };
  }, [mapOpen, routeId, roadLegs]);

  const pingLocation = useCallback((lat, lng) => {
    apiClient.post(`/driver/routes/${routeId}/location`, { lat, lng }).catch(() => {});
  }, [routeId]);

  const startWatching = useCallback(() => {
    if (watchIdRef.current || !navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => pingLocation(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    );
  }, [pingLocation]);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (state.route?.tracking_enabled) startWatching();
    return stopWatching;
  }, [state.route?.tracking_enabled, startWatching, stopWatching]);

  const handleStart = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/driver/routes/${routeId}/start`);
      toast.success("Route started — your location is now shared while this route is in progress.");
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not start route."));
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async () => {
    setBusy(true);
    try {
      stopWatching();
      await apiClient.post(`/driver/routes/${routeId}/finish`);
      toast.success("Route finished.");
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not finish route."));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="driver-route-detail-page">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-4 h-20 w-full" />
      </div>
    );
  }

  if (state.error || !state.route) {
    return (
      <div data-testid="driver-route-detail-page">
        <ErrorState message={state.error || "Route not found."} onRetry={load} />
      </div>
    );
  }

  const r = state.route;
  const skippedCount = r.stops.filter((s) => s.packing_status === 2).length;
  const actualDeliveries = r.stops.length - skippedCount;

  return (
    <div data-testid="driver-route-detail-page">
      <SyncIndicator />
      <button type="button" onClick={() => navigate("/driver")} className="mb-3 flex items-center gap-1 text-xs text-text-muted" data-testid="driver-route-back-button">
        <ArrowLeft size={14} strokeWidth={1.5} /> All routes
      </button>

      <div className="mb-4 rounded-md border border-border bg-surface p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-text-main">Route #{r.id}</p>
          <p className="text-xs text-text-muted">{r.deliveries_done}/{r.deliveries_total} done</p>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="flex items-center justify-center gap-1.5 rounded-full bg-success-bg px-3 py-2 text-xs font-semibold text-success" data-testid="driver-route-actual-deliveries-pill">
            <Truck size={14} strokeWidth={1.5} /> {actualDeliveries} Deliveries
          </div>
          <div className="flex items-center justify-center gap-1.5 rounded-full bg-danger-bg px-3 py-2 text-xs font-semibold text-danger" data-testid="driver-route-skipped-pill">
            <PackageX size={14} strokeWidth={1.5} /> {skippedCount} Skipped
          </div>
        </div>
        {r.driver_map_enabled && (
          <Button
            variant="outline"
            className="mt-3 w-full"
            onClick={() => setMapOpen(true)}
            data-testid="driver-route-view-map-button"
          >
            <Map size={16} strokeWidth={1.5} /> View Map
          </Button>
        )}
        <div className="mt-3">
          {!r.started_at && (
            <Button className="w-full" onClick={handleStart} disabled={busy} data-testid="driver-route-start-button">
              <Play size={16} strokeWidth={1.5} /> Start Route
            </Button>
          )}
          {r.started_at && !r.finished_at && (
            <Button variant="danger" className="w-full" onClick={handleFinish} disabled={busy} data-testid="driver-route-finish-button">
              <Square size={16} strokeWidth={1.5} /> Finish Route
            </Button>
          )}
          {r.finished_at && (
            <div className="space-y-2">
              <p className="text-center text-xs font-medium text-success" data-testid="driver-route-finished-label">Route finished</p>
              <Button variant="outline" className="w-full" onClick={handleStart} disabled={busy} data-testid="driver-route-resume-button">
                <Play size={16} strokeWidth={1.5} /> Resume Route
              </Button>
            </div>
          )}
        </div>
      </div>

      <h2 className="mb-2 text-sm font-semibold text-text-main">Stops (in order)</h2>
      <div className="space-y-2">
        {r.stops.map((stop) => {
          const override = getLocalOverride(stop.id);
          const effectiveStatus = override?.delivery_status ?? stop.delivery_status;
          const skippedByPacking = stop.packing_status === 2;
          const meta = skippedByPacking ? packingSkippedMeta() : stopMeta(effectiveStatus);
          const hasCoords = stop.latitude && stop.longitude;
          const cashCollected = getCashOverride(stop.id)?.collected || stop.payment_state === "collected";
          const cashPending = !cashCollected && stop.payment_state === "cash_pending";
          return (
            <div
              key={stop.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/driver/routes/${routeId}/stops/${stop.id}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/driver/routes/${routeId}/stops/${stop.id}`)}
              className="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-surface p-3 transition-colors active:bg-background"
              data-testid={`driver-stop-card-${stop.id}`}
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary" data-testid={`driver-stop-order-${stop.id}`}>
                #{stop.delivery_order_id}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-main">
                {stop.customer_name || `Delivery ${stop.id}`}
              </span>
              {cashPending && (
                <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-danger-bg px-2 py-0.5 text-[11px] font-semibold text-danger" data-testid={`driver-stop-cash-flag-${stop.id}`}>
                  <HandCoins size={12} strokeWidth={1.5} /> Cash
                </span>
              )}
              {hasCoords && (
                <a
                  href={mapsLink(stop.latitude, stop.longitude)} target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex flex-shrink-0 items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-text-main"
                  data-testid={`driver-stop-navigate-${stop.id}`}
                >
                  <Navigation2 size={13} strokeWidth={1.5} /> Navigate
                </a>
              )}
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.color}`} data-testid={`driver-stop-status-${stop.id}`}>
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>

      {mapOpen && (
        <div
          className="fixed inset-0 z-[1000] flex flex-col bg-black/60"
          data-testid="driver-route-map-overlay"
          onClick={() => setMapOpen(false)}
        >
          <div
            className="mx-auto mt-auto flex h-[88vh] w-full flex-col overflow-hidden rounded-t-xl bg-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-text-main">Route #{r.id} — Map</p>
              <button
                type="button"
                onClick={() => setMapOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted active:bg-background"
                data-testid="driver-route-map-close-button"
                aria-label="Close map"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <RouteMap
                outlet={r.outlet}
                driver={null}
                stops={r.stops.map((s) => ({
                  ...s,
                  delivery_status: getLocalOverride(s.id)?.delivery_status ?? s.delivery_status,
                }))}
                live={null}
                trackingEnabled={false}
                roadLegs={roadLegs}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
